/**
 * useVoiceStream — постоянная запись с микрофона с определением пауз.
 *
 * Пишем непрерывно, но наружу отдаём не «куски по таймеру», а законченные
 * фразы: как только речь замолкла на паузу (по умолчанию 0.8 с), накопленный
 * фрагмент уходит на распознавание. Запись при этом не останавливается,
 * поэтому следующая фраза сразу попадает в тот же поток.
 *
 * MediaRecorder здесь не годится: его фрагменты webm по отдельности не
 * декодируются. Поэтому AudioWorklet считает PCM, а JS собирает WAV.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Частота, на которой распознавание работает лучше всего. */
const TARGET_SAMPLE_RATE = 16000;

/** Пауза, после которой фраза считается законченной. */
const SILENCE_SECONDS = 0.8;
/** Минимальная длительность речи, чтобы фрагмент имел смысл. */
const MIN_SPEECH_SECONDS = 0.35;
/** Верхняя граница одного фрагмента на случай непрерывной речи. */
const MAX_SEGMENT_SECONDS = 20;

const WORKLET_SOURCE = `
class AiaVadCollector extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = (options && options.processorOptions) || {};
    this.maxSamples = Math.round(sampleRate * (o.maxSeconds || 20));
    this.silenceSamples = Math.round(sampleRate * (o.silenceSeconds || 0.8));
    this.minSpeechSamples = Math.round(sampleRate * (o.minSpeechSeconds || 0.35));
    this.buffer = new Float32Array(this.maxSamples);
    this.filled = 0;
    this.speech = 0;
    this.silence = 0;
    this.active = false;
    this.noise = 0.004;
    this.port.onmessage = (event) => {
      const data = event.data;
      if (!data) return;
      if (data.type === "flush") this.emit();
    };
  }

  emit() {
    if (this.filled <= 0) {
      this.reset();
      return;
    }
    // Срезаем хвостовую тишину, она только мешает распознаванию.
    const trim = Math.min(this.silence, this.silenceSamples);
    const length = Math.max(0, this.filled - trim);
    const samples = this.buffer.slice(0, length);
    const voiced = this.speech >= this.minSpeechSamples;
    this.reset();
    if (length > 0 && voiced) {
      this.port.postMessage({ type: "segment", samples: samples });
    }
  }

  reset() {
    this.filled = 0;
    this.speech = 0;
    this.silence = 0;
    this.active = false;
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || !input.length || !input[0]) return true;
    const channels = input.length;
    const frames = input[0].length;

    let sumSquares = 0;
    for (let i = 0; i < frames; i += 1) {
      const value = input[0][i] || 0;
      sumSquares += value * value;
    }
    const rms = Math.sqrt(sumSquares / frames);

    // Адаптивный порог: шумовой фон не должен запускать запись.
    if (!this.active) {
      this.noise = this.noise * 0.97 + rms * 0.03;
    }
    const threshold = Math.max(0.01, this.noise * 2.2);

    for (let i = 0; i < frames; i += 1) {
      if (this.filled >= this.maxSamples) break;
      let value = 0;
      for (let ch = 0; ch < channels; ch += 1) {
        value += input[ch][i] || 0;
      }
      this.buffer[this.filled] = value / channels;
      this.filled += 1;
    }

    if (rms >= threshold) {
      if (!this.active) {
        // Начало новой фразы: клиент сбрасывает отложенную отправку.
        this.port.postMessage({ type: "speech" });
      }
      this.speech += frames;
      this.silence = 0;
      this.active = true;
    } else if (this.active) {
      this.silence += frames;
    }

    if (this.active && this.silence >= this.silenceSamples && this.speech >= this.minSpeechSamples) {
      this.emit();
      return true;
    }
    if (this.filled >= this.maxSamples) {
      this.emit();
    }
    return true;
  }
}

registerProcessor("aia-vad-collector", AiaVadCollector);
`;

function writeAscii(view, offset, text) {
    for (let i = 0; i < text.length; i += 1) {
        view.setUint8(offset + i, text.charCodeAt(i));
    }
}

/** Float32 [-1..1] -> WAV 16 бит моно. */
function encodeWav(samples, sampleRate) {
    const rate = Math.max(8000, Math.round(sampleRate || TARGET_SAMPLE_RATE));
    const bytes = samples.length * 2;
    const buffer = new ArrayBuffer(44 + bytes);
    const view = new DataView(buffer);

    writeAscii(view, 0, "RIFF");
    view.setUint32(4, 36 + bytes, true);
    writeAscii(view, 8, "WAVE");
    writeAscii(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, rate, true);
    view.setUint32(28, rate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeAscii(view, 36, "data");
    view.setUint32(40, bytes, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i += 1, offset += 2) {
        const sample = Math.max(-1, Math.min(1, samples[i] || 0));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    }
    return new Blob([view], { type: "audio/wav" });
}

export default function useVoiceStream({ onSegment, onSpeechStart, silenceSeconds = SILENCE_SECONDS } = {}) {
    const [active, setActive] = useState(false);
    const [level, setLevel] = useState(0);
    const [error, setError] = useState("");

    const contextRef = useRef(null);
    const streamRef = useRef(null);
    const nodeRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(0);
    const levelTickRef = useRef(0);
    const onSegmentRef = useRef(onSegment);

    useEffect(() => {
        onSegmentRef.current = onSegment;
    }, [onSegment]);

    const onSpeechStartRef = useRef(onSpeechStart);

    useEffect(() => {
        onSpeechStartRef.current = onSpeechStart;
    }, [onSpeechStart]);

    const emit = useCallback((samples) => {
        if (!samples || !samples.length) return;
        const context = contextRef.current;
        const sampleRate = context ? context.sampleRate : TARGET_SAMPLE_RATE;
        // Слишком короткий фрагмент — почти наверняка шум.
        if (samples.length < sampleRate * 0.25) return;
        const blob = encodeWav(samples, sampleRate);
        if (blob.size <= 44) return;
        if (onSegmentRef.current) {
            onSegmentRef.current(blob);
        }
    }, []);

    const stopMeter = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        }
        setLevel(0);
    }, []);

    const cleanup = useCallback(() => {
        stopMeter();
        const node = nodeRef.current;
        if (node) {
            try {
                node.port.onmessage = null;
                node.disconnect();
            } catch (_) {
                // узел уже отключён
            }
        }
        nodeRef.current = null;
        analyserRef.current = null;

        const stream = streamRef.current;
        if (stream) {
            try {
                stream.getTracks().forEach((track) => track.stop());
            } catch (_) {
                // поток уже остановлен
            }
        }
        streamRef.current = null;

        const context = contextRef.current;
        if (context) {
            try {
                context.close();
            } catch (_) {
                // контекст уже закрыт
            }
        }
        contextRef.current = null;
        setActive(false);
    }, [stopMeter]);

    const startMeter = useCallback((analyser) => {
        const buffer = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
            analyser.getByteFrequencyData(buffer);
            const now = Date.now();
            if (now - levelTickRef.current > 90) {
                levelTickRef.current = now;
                let sum = 0;
                for (let i = 0; i < buffer.length; i += 1) {
                    sum += buffer[i];
                }
                const average = sum / buffer.length / 110;
                setLevel(Math.max(0, Math.min(1, average)));
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    }, []);

    const start = useCallback(async () => {
        if (contextRef.current) return true;
        setError("");

        if (!navigator.mediaDevices?.getUserMedia) {
            setError("Браузер не поддерживает запись с микрофона.");
            return false;
        }

        let stream = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                },
            });
        } catch (_) {
            setError("Нет доступа к микрофону.");
            return false;
        }

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
            stream.getTracks().forEach((track) => track.stop());
            setError("Браузер не поддерживает обработку звука.");
            return false;
        }

        let context = null;
        try {
            context = new AudioContextClass({ sampleRate: TARGET_SAMPLE_RATE });
        } catch (_) {
            context = new AudioContextClass();
        }

        try {
            await context.resume();
            const moduleUrl = URL.createObjectURL(
                new Blob([WORKLET_SOURCE], { type: "application/javascript" }),
            );
            await context.audioWorklet.addModule(moduleUrl);
            URL.revokeObjectURL(moduleUrl);
        } catch (_) {
            try {
                context.close();
            } catch (_) {
                // игнорируем
            }
            stream.getTracks().forEach((track) => track.stop());
            setError("Не удалось подготовить обработку звука.");
            return false;
        }

        const source = context.createMediaStreamSource(stream);
        const node = new AudioWorkletNode(context, "aia-vad-collector", {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            processorOptions: {
                maxSeconds: MAX_SEGMENT_SECONDS,
                silenceSeconds,
                minSpeechSeconds: MIN_SPEECH_SECONDS,
            },
        });

        node.port.onmessage = (event) => {
            const data = event.data;
            if (!data) return;
            if (data.type === "segment") {
                emit(data.samples);
                return;
            }
            if (data.type === "speech" && onSpeechStartRef.current) {
                onSpeechStartRef.current();
            }
        };

        // Worklet обрабатывает вход только при подключении к выходу.
        const sink = context.createGain();
        sink.gain.value = 0;
        source.connect(node);
        node.connect(sink);
        sink.connect(context.destination);

        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);

        contextRef.current = context;
        streamRef.current = stream;
        nodeRef.current = node;
        analyserRef.current = analyser;
        setActive(true);
        startMeter(analyser);
        return true;
    }, [emit, silenceSeconds, startMeter]);

    /** Остановка: доотправляем то, что ещё не успело превратиться в фразу. */
    const stop = useCallback(async () => {
        const node = nodeRef.current;
        if (node) {
            try {
                node.port.postMessage({ type: "flush" });
            } catch (_) {
                // узел уже отвалился
            }
            // Даём ворклету кадр на отправку остатка.
            await new Promise((resolve) => window.setTimeout(resolve, 180));
        }
        cleanup();
    }, [cleanup]);

    const cancel = useCallback(() => {
        cleanup();
    }, [cleanup]);

    useEffect(() => () => cleanup(), [cleanup]);

    return { active, level, error, start, stop, cancel };
}

export { SILENCE_SECONDS };
